import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Paperclip, Clock, Link2, X, Check, Loader2,
  Search, ChevronDown, ChevronUp, AlertTriangle,
  Download, Eye, Building2, Trash2, CheckSquare,
  Square, Filter, RotateCcw,
} from 'lucide-react';
import { emailApiService, type UnmatchedEmail } from '../../services/email/emailApiService';
import { getTenders } from '../../services/tenderService';
import { getActiveRetailers, type Retailer } from '../../services/retailerService';
import { useToast } from '../../../../foundation/altzero_base/base/hooks/use-toast';
import type { Tender } from '../../services/tenderService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelative(dateStr: string) {
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  <  1)  return 'just now';
  if (mins  < 60)  return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days  <  7)  return `${days}d ago`;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
}

function getInitials(name: string | null, email: string) {
  const src = name || email;
  return src.split(/[\s@.]+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
}

const AVATAR_PALETTE = [
  'bg-violet-500', 'bg-rose-500', 'bg-amber-500',
  'bg-cyan-500', 'bg-emerald-500', 'bg-indigo-500',
];
function avatarColor(email: string) {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = email.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

const ACTIVE_STATUSES: string[] = [
  'draft', 'tender_ready', 'waiting_for_retailers_reply', 'retailers_replied', 'under_review',
];

function formatBytes(bytes: number) {
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes > 1024)        return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

// Some senders (e.g. Shell, Flo) declare a generic Content-Type — application/octet-stream,
// application/x-pdf — instead of the exact registered MIME type, so a strict mimeType check
// misses previewable files. Fall back to the filename extension in that case.
const VIEWABLE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp'];

function isViewable(mimeType: string, filename?: string) {
  if (mimeType.startsWith('image/') || mimeType === 'application/pdf') return true;
  const lower = filename?.toLowerCase() ?? '';
  return VIEWABLE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** Try to match a sender email to a known retailer (returns retailer.id or null) */
function detectRetailer(senderEmail: string, retailers: Retailer[]): string | null {
  const raw = senderEmail.match(/<([^>]+)>/)?.[1] ?? senderEmail;
  const addr = raw.toLowerCase();
  const domain = addr.split('@')[1];
  for (const r of retailers) {
    const rAddr = r.contact_email?.toLowerCase();
    if (rAddr === addr) return r.id;
    if (r.contact_emails?.some((e: string) => e.toLowerCase() === addr)) return r.id;
    if (r.email_domains?.some((d: string) => d.toLowerCase() === domain)) return r.id;
    if (rAddr?.split('@')[1] === domain) return r.id;
  }
  return null;
}

// ─── Attachment row ───────────────────────────────────────────────────────────

type AttachmentInfo = { filename: string; mimeType: string; attachmentId: string; size: number };

function AttachmentRow({ att, accountId }: { att: AttachmentInfo; accountId: string }) {
  const [dlState, setDlState] = useState<'idle' | 'loading' | 'done'>('idle');

  const triggerBlob = async (mode: 'download' | 'view') => {
    setDlState('loading');
    try {
      const blob = await emailApiService.downloadAttachment(att.attachmentId, accountId);
      const url  = URL.createObjectURL(blob);
      if (mode === 'view') {
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = att.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
      setDlState('done');
      setTimeout(() => setDlState('idle'), 2000);
    } catch {
      setDlState('idle');
    }
  };

  return (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm group">
      <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700 truncate leading-tight">{att.filename}</p>
        {att.size > 0 && <p className="text-xs text-gray-400">{formatBytes(att.size)}</p>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {isViewable(att.mimeType, att.filename) && (
          <button
            onClick={() => triggerBlob('view')}
            disabled={dlState === 'loading'}
            title="Preview"
            className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-40"
          >
            {dlState === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        )}
        <button
          onClick={() => triggerBlob('download')}
          disabled={dlState === 'loading'}
          title="Download"
          className="p-1.5 rounded-md text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
        >
          {dlState === 'done'
            ? <Check className="w-3.5 h-3.5 text-emerald-500" />
            : dlState === 'loading' && !isViewable(att.mimeType, att.filename)
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Download className="w-3.5 h-3.5" />
          }
        </button>
      </div>
    </div>
  );
}

// ─── Searchable dropdown ──────────────────────────────────────────────────────

interface DropdownProps<T> {
  value: string;
  onChange: (id: string) => void;
  options: T[];
  getLabel: (item: T) => string;
  getSub?: (item: T) => string | null;
  getId: (item: T) => string;
  placeholder: string;
  disabled?: boolean;
  accent?: string;
}

function SearchableDropdown<T>({
  value, onChange, options, getLabel, getSub, getId,
  placeholder, disabled, accent,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = options.filter(o => {
    if (!q) return true;
    const lq = q.toLowerCase();
    return getLabel(o).toLowerCase().includes(lq) || (getSub?.(o) || '').toLowerCase().includes(lq);
  });

  const selected = options.find(o => getId(o) === value);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all
          ${value
            ? 'border-blue-300 bg-blue-50 text-blue-800 font-medium'
            : 'border-gray-200 bg-gray-50 text-gray-400 hover:border-blue-300 hover:bg-white'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className="flex items-center gap-2 truncate">
          {accent && value && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${accent}`} />}
          <span className="truncate">{selected ? getLabel(selected) : placeholder}</span>
        </span>
        {open
          ? <ChevronUp   className="w-4 h-4 flex-shrink-0 text-gray-400" />
          : <ChevronDown className="w-4 h-4 flex-shrink-0 text-gray-400" />
        }
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.13 }}
            className="absolute z-50 bottom-full mb-2 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
          >
            <div className="px-3 py-2.5 border-b border-gray-100">
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  placeholder={`Search ${placeholder.toLowerCase()}…`}
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
                />
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-5">No results found</p>
              ) : (
                filtered.map(o => {
                  const id  = getId(o);
                  const lbl = getLabel(o);
                  const sub = getSub?.(o);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => { onChange(id); setOpen(false); setQ(''); }}
                      className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center justify-between gap-3
                        ${id === value ? 'bg-blue-50' : ''}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{lbl}</p>
                        {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
                      </div>
                      {id === value && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Delete confirm modal ─────────────────────────────────────────────────────

interface DeleteConfirmProps {
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

function DeleteConfirmModal({ count, onConfirm, onCancel, isDeleting }: DeleteConfirmProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.94 }}
        className="bg-white rounded-2xl shadow-2xl p-6 w-80 mx-4"
      >
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Trash2 className="w-6 h-6 text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-900 text-center mb-1">
          Delete {count} email{count !== 1 ? 's' : ''}?
        </h3>
        <p className="text-sm text-gray-500 text-center mb-5">
          This will permanently remove {count === 1 ? 'this email' : 'these emails'} from the unlinked inbox. This cannot be undone.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {isDeleting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Deleting…</>
            ) : (
              <><Trash2 className="w-4 h-4" /> Delete</>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface UnlinkedEmailsTabProps {
  onCountChange?: (count: number) => void;
  onAssigned?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const UnlinkedEmailsTab: React.FC<UnlinkedEmailsTabProps> = ({
  onCountChange,
  onAssigned,
}) => {
  const { toast } = useToast();

  const [emails,    setEmails]    = useState<UnmatchedEmail[]>([]);
  const [tenders,   setTenders]   = useState<Tender[]>([]);
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // ── Filter ──────────────────────────────────────────────────────────────────
  const [filterQuery, setFilterQuery] = useState('');

  const filteredEmails = useMemo(() => {
    if (!filterQuery.trim()) return emails;
    const q = filterQuery.toLowerCase();
    return emails.filter(e =>
      e.sender_email.toLowerCase().includes(q) ||
      (e.sender_name?.toLowerCase() || '').includes(q) ||
      (e.subject?.toLowerCase() || '').includes(q)
    );
  }, [emails, filterQuery]);

  // ── Bulk selection ──────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ ids: string[] } | null>(null);

  const isAllSelected = filteredEmails.length > 0 && filteredEmails.every(e => selectedIds.has(e.id));
  const isIndeterminate = selectedIds.size > 0 && !isAllSelected;

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (isAllSelected) return new Set();
      return new Set(filteredEmails.map(e => e.id));
    });
  }, [isAllSelected, filteredEmails]);

  const toggleSelect = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Clear selection when filter changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filterQuery]);

  // ── Assign state ────────────────────────────────────────────────────────────
  const [assignState,        setAssignState]        = useState<'idle' | 'assigning' | 'assigned' | 'ignoring'>('idle');
  const [selectedTenderId,   setSelectedTenderId]   = useState('');
  const [selectedRetailerId, setSelectedRetailerId] = useState('');

  // ── Data loading ────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [emailsRes, tendersRes, retailersRes] = await Promise.all([
        emailApiService.getUnmatchedEmails(),
        getTenders(),
        getActiveRetailers(),
      ]);

      setEmails(emailsRes);
      onCountChange?.(emailsRes.length);

      if (tendersRes.success && tendersRes.data) {
        const active = tendersRes.data.filter(t => !t.status || ACTIVE_STATUSES.includes(t.status));
        setTenders(active.length ? active : tendersRes.data);
      }
      if (retailersRes.success && retailersRes.data) {
        setRetailers(retailersRes.data);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  // Auto-select first email
  useEffect(() => {
    if (!selectedId && filteredEmails.length > 0) setSelectedId(filteredEmails[0].id);
  }, [filteredEmails, selectedId]);

  const selectedEmail = emails.find(e => e.id === selectedId) ?? null;

  // Reset assign controls on email change
  useEffect(() => {
    setAssignState('idle');
    setSelectedTenderId('');
    setSelectedRetailerId('');
  }, [selectedId]);

  // Auto-detect retailer
  useEffect(() => {
    if (!selectedEmail || retailers.length === 0) return;
    const detected = detectRetailer(selectedEmail.sender_email, retailers);
    if (detected) setSelectedRetailerId(detected);
  }, [selectedId, retailers]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const removeEmails = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    setEmails(prev => {
      const next = prev.filter(e => !idSet.has(e.id));
      onCountChange?.(next.length);
      // Advance selection if current email was removed
      if (selectedId && idSet.has(selectedId)) {
        const idx = prev.findIndex(e => e.id === selectedId);
        const remaining = prev.filter(e => !idSet.has(e.id));
        setSelectedId(remaining[Math.min(idx, remaining.length - 1)]?.id ?? null);
      }
      return next;
    });
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  }, [selectedId, onCountChange]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  /** Delete a single email (individual delete button) */
  const handleDeleteOne = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Optimistic remove
    removeEmails([id]);
    try {
      await emailApiService.deleteUnmatchedEmail(id);
    } catch {
      toast({ title: 'Delete failed', description: 'Could not delete email. Please try again.', variant: 'destructive' });
      load(); // reload to restore
    }
  }, [removeEmails, load, toast]);

  /** Confirm + execute bulk delete */
  const handleBulkDelete = async () => {
    if (!confirmDelete) return;
    const ids = confirmDelete.ids;
    setBulkDeleting(true);
    try {
      await emailApiService.bulkDeleteUnmatchedEmails(ids);
      removeEmails(ids);
      setConfirmDelete(null);
      toast({ title: `Deleted ${ids.length} email${ids.length !== 1 ? 's' : ''}` });
    } catch {
      toast({ title: 'Bulk delete failed', description: 'Could not delete emails. Please try again.', variant: 'destructive' });
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedId || !selectedTenderId) return;
    setAssignState('assigning');
    try {
      await emailApiService.assignUnmatchedEmail(
        selectedId,
        selectedTenderId,
        selectedRetailerId || undefined,
      );
      setAssignState('assigned');
      onAssigned?.();
      setTimeout(() => removeEmails([selectedId]), 900);
    } catch {
      setAssignState('idle');
      toast({ title: 'Assign failed', description: 'Could not assign email. Please try again.', variant: 'destructive' });
    }
  };

  const handleIgnore = async () => {
    if (!selectedId) return;
    setAssignState('ignoring');
    try {
      await emailApiService.ignoreUnmatchedEmail(selectedId);
      setTimeout(() => removeEmails([selectedId]), 500);
    } catch {
      setAssignState('idle');
    }
  };

  const isWorking = assignState === 'assigning' || assignState === 'ignoring';

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-gray-300 animate-spin" />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-10">
        <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4 shadow-sm">
          <Check className="w-8 h-8 text-emerald-400" />
        </div>
        <p className="text-base font-semibold text-gray-600">All caught up!</p>
        <p className="text-sm text-gray-400 mt-1">No unlinked emails to review</p>
      </div>
    );
  }

  // ── Main ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="flex flex-1 overflow-hidden">

        {/* ══ Left panel: email list ══════════════════════════════════════════ */}
        <div className="w-72 flex-shrink-0 border-r bg-white flex flex-col overflow-hidden">

          {/* Header count */}
          <div className="px-4 py-3 border-b bg-amber-50 flex items-center gap-2 flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
              {emails.length} Unlinked email{emails.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={load}
              title="Refresh"
              className="ml-auto p-1 rounded-md text-amber-400 hover:text-amber-600 hover:bg-amber-100 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Search / filter */}
          <div className="px-3 py-2.5 border-b bg-white flex-shrink-0">
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100 transition-all">
              <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={filterQuery}
                onChange={e => setFilterQuery(e.target.value)}
                placeholder="Filter by sender, subject…"
                className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder-gray-400"
              />
              {filterQuery && (
                <button
                  onClick={() => setFilterQuery('')}
                  className="text-gray-300 hover:text-gray-500 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {filterQuery && (
              <p className="text-[10px] text-gray-400 mt-1.5 pl-1">
                {filteredEmails.length} of {emails.length} match
              </p>
            )}
          </div>

          {/* Bulk action bar — only when items are checked */}
          <AnimatePresence>
            {selectedIds.size > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="overflow-hidden flex-shrink-0"
              >
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-100">
                  {/* Select all toggle */}
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-800 flex-shrink-0"
                    title={isAllSelected ? 'Deselect all' : 'Select all'}
                  >
                    {isAllSelected
                      ? <CheckSquare className="w-3.5 h-3.5" />
                      : <Square className="w-3.5 h-3.5" />
                    }
                    {selectedIds.size}
                  </button>

                  <span className="text-xs text-blue-500 flex-1 truncate">
                    email{selectedIds.size !== 1 ? 's' : ''} selected
                  </span>

                  {/* Bulk delete */}
                  <button
                    onClick={() => setConfirmDelete({ ids: Array.from(selectedIds) })}
                    className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors flex-shrink-0 shadow-sm"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete {selectedIds.size}
                  </button>

                  {/* Deselect */}
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    title="Clear selection"
                    className="p-1 rounded-md text-blue-400 hover:text-blue-700 hover:bg-blue-100 transition-colors flex-shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Select-all row (always shown when no bulk bar) */}
          {selectedIds.size === 0 && filteredEmails.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-1.5 border-b bg-gray-50 flex-shrink-0">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Square className="w-3 h-3" />
                Select all
              </button>
            </div>
          )}

          {/* Email list */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {filteredEmails.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <Filter className="w-6 h-6 text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">No emails match the filter</p>
                <button
                  onClick={() => setFilterQuery('')}
                  className="mt-2 text-xs text-blue-500 hover:underline"
                >
                  Clear filter
                </button>
              </div>
            ) : (
              <AnimatePresence mode="popLayout">
                {filteredEmails.map(email => {
                  const isSelected  = email.id === selectedId;
                  const isChecked   = selectedIds.has(email.id);
                  const initials    = getInitials(email.sender_name, email.sender_email);
                  const color       = avatarColor(email.sender_email);
                  const name        = email.sender_name || email.sender_email;

                  return (
                    <motion.div
                      key={email.id}
                      layout
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className={`relative flex items-start gap-2.5 px-3 py-3 cursor-pointer transition-colors group
                        ${isSelected
                          ? 'bg-blue-50 border-l-[3px] border-l-blue-500'
                          : 'hover:bg-gray-50 border-l-[3px] border-l-transparent'
                        }
                        ${isChecked && !isSelected ? 'bg-blue-50/40' : ''}
                      `}
                      onClick={() => setSelectedId(email.id)}
                    >
                      {/* Checkbox */}
                      <button
                        onClick={e => toggleSelect(email.id, e)}
                        className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-all
                          ${isChecked
                            ? 'bg-blue-500 border-blue-500 text-white'
                            : 'border-gray-300 bg-white group-hover:border-blue-400'
                          }`}
                      >
                        {isChecked && <Check className="w-2.5 h-2.5" />}
                      </button>

                      {/* Avatar */}
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full ${color} flex items-center justify-center text-white text-xs font-bold shadow-sm`}>
                        {initials || <Mail className="w-3.5 h-3.5" />}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1 mb-0.5">
                          <p className={`text-sm font-semibold truncate ${isSelected ? 'text-blue-800' : 'text-gray-800'}`}>
                            {name}
                          </p>
                          <span className="text-[10px] text-gray-400 flex-shrink-0">
                            {formatRelative(email.received_at)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 font-medium truncate">{email.subject || '(no subject)'}</p>
                        {email.snippet && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">{email.snippet}</p>
                        )}
                        {email.attachment_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-blue-500 mt-1 font-medium">
                            <Paperclip className="w-2.5 h-2.5" />
                            {email.attachment_count} file{email.attachment_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {/* Individual delete (hover-revealed) */}
                      <button
                        onClick={e => handleDeleteOne(email.id, e)}
                        title="Delete email"
                        className="flex-shrink-0 p-1 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 mt-0.5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </div>
        </div>

        {/* ══ Right panel: email detail ════════════════════════════════════════ */}
        {selectedEmail ? (
          <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">

            {/* Email header */}
            <div className="bg-white border-b px-6 py-4 flex-shrink-0">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-base font-semibold text-gray-900 leading-tight flex-1 min-w-0">
                  {selectedEmail.subject || '(no subject)'}
                </h3>
                {/* Delete this email */}
                <button
                  onClick={e => handleDeleteOne(selectedEmail.id, e)}
                  title="Delete this email"
                  className="flex-shrink-0 flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors border border-transparent hover:border-red-100"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full ${avatarColor(selectedEmail.sender_email)} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>
                    {getInitials(selectedEmail.sender_name, selectedEmail.sender_email) || <Mail className="w-3 h-3" />}
                  </div>
                  <span className="font-medium text-gray-700">
                    {selectedEmail.sender_name || selectedEmail.sender_email}
                  </span>
                  {selectedEmail.sender_name && (
                    <span className="text-gray-400 text-xs">&lt;{selectedEmail.sender_email}&gt;</span>
                  )}
                </div>
                <span className="flex items-center gap-1.5 text-gray-400 text-xs">
                  <Clock className="w-3.5 h-3.5" />
                  {formatRelative(selectedEmail.received_at)}
                </span>
                {selectedEmail.attachment_count > 0 && (
                  <span className="flex items-center gap-1.5 text-blue-500 text-xs font-medium">
                    <Paperclip className="w-3.5 h-3.5" />
                    {selectedEmail.attachment_count} attachment{selectedEmail.attachment_count !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Email body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                {selectedEmail.body_text ? (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                    {selectedEmail.body_text}
                  </p>
                ) : selectedEmail.snippet ? (
                  <p className="text-sm text-gray-500 italic leading-relaxed">{selectedEmail.snippet}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic">No email content available</p>
                )}
              </div>

              {selectedEmail.attachments.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
                    Attachments
                  </p>
                  <div className="flex flex-col gap-2">
                    {selectedEmail.attachments.map((att, i) => (
                      <AttachmentRow
                        key={`${att.attachmentId}-${i}`}
                        att={att}
                        accountId={selectedEmail.account_id}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Assign footer */}
            <div className="bg-white border-t px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Link2 className="w-4 h-4 text-blue-500" />
                  Assign to Tender
                </p>
                <button
                  onClick={handleIgnore}
                  disabled={isWorking}
                  className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-amber-600 transition-colors disabled:opacity-40 px-2 py-1 rounded-md hover:bg-amber-50"
                >
                  {assignState === 'ignoring'
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <X className="w-3.5 h-3.5" />
                  }
                  Dismiss
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Tender *</label>
                  <SearchableDropdown<Tender>
                    value={selectedTenderId}
                    onChange={setSelectedTenderId}
                    options={tenders}
                    getId={t => t.id}
                    getLabel={t => t.opportunity_name || `Tender ${t.id.slice(0, 6)}`}
                    getSub={t => t.client?.name ?? null}
                    placeholder="Select tender…"
                    disabled={isWorking}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    Retailer
                    {selectedRetailerId && (
                      <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.5 rounded-full ml-1">
                        auto-detected
                      </span>
                    )}
                  </label>
                  <SearchableDropdown<Retailer>
                    value={selectedRetailerId}
                    onChange={setSelectedRetailerId}
                    options={retailers}
                    getId={r => r.id}
                    getLabel={r => r.name}
                    getSub={r => r.contact_email ?? null}
                    placeholder="Select retailer…"
                    disabled={isWorking}
                    accent="bg-emerald-400"
                  />
                </div>
              </div>

              <button
                onClick={handleAssign}
                disabled={!selectedTenderId || isWorking || assignState === 'assigned'}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm
                  ${assignState === 'assigned'
                    ? 'bg-emerald-500 text-white cursor-default'
                    : selectedTenderId && !isWorking
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                  }`}
              >
                {assignState === 'assigning' ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Assigning…</>
                ) : assignState === 'assigned' ? (
                  <><Check className="w-4 h-4" /> Assigned!</>
                ) : (
                  <><Link2 className="w-4 h-4" /> Assign to Tender</>
                )}
              </button>
              {!selectedTenderId && (
                <p className="text-xs text-gray-400 text-center mt-1.5">Select a tender to continue</p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-gray-400">Select an email from the list</p>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {confirmDelete && (
          <DeleteConfirmModal
            count={confirmDelete.ids.length}
            onConfirm={handleBulkDelete}
            onCancel={() => !bulkDeleting && setConfirmDelete(null)}
            isDeleting={bulkDeleting}
          />
        )}
      </AnimatePresence>
    </>
  );
};
